import React, {Component, useLayoutEffect, useRef, useState} from "react";
import {Card, Col, Row} from "react-bootstrap";

// Fixed rotation interval for news items (20 seconds)
const NEWS_ROTATE_MS = 20000;

// Small utility to auto-shrink text so it fits into one line within its container
const AutoFitText = ({text, max = 18, min = 12, step = 1, className = '', style = {}}) => {
    const ref = useRef(null);
    const [fontSize, setFontSize] = useState(max);

    const measure = () => {
        const el = ref.current;
        if (!el) return;
        // reset to max first for re-measure on resize
        el.style.fontSize = max + 'px';
        let current = max;
        // Shrink until it fits in one line or reach min
        // Condition: scrollWidth <= clientWidth (fits)
        while (current > min && el.scrollWidth > el.clientWidth + 1) {
            current -= step;
            el.style.fontSize = current + 'px';
        }
        setFontSize(current);
    };

    useLayoutEffect(() => {
        measure();
        const onResize = () => measure();
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [text, max, min, step]);

    return (
        <span
            ref={ref}
            className={className}
            style={{
                display: 'block',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                fontSize: fontSize,
                ...style
            }}
            title={typeof text === 'string' ? text : undefined}
        >
            {text}
        </span>
    );
};

const GeneralInfoCard = ({title, value, Icon}) => (
    <Card className="mb-2 p-4 general-info-card" style={{borderColor: '#91D5FF80', borderRadius: '10px'}}>
        <div className="d-flex align-items-center h-100 w-100">
            <img height="40px" src={`img/${Icon}.svg`} className="text-primary me-3"/>
            <div className="flex-grow-1" style={{minWidth: 0}}>
                <small className="text-muted" style={{fontSize: '0.8rem'}}>
                    {title}
                </small>
                <p className="mb-0 fw-bolder" style={{color: '#2C6A8C'}}>
                    <AutoFitText text={value} max={18} min={12}/>
                </p>
            </div>
        </div>
    </Card>
);

export default class PosInfo extends Component {

    state = {
        pos: {
            appInfo: {},
            merchants: [],
        },
        isMedicine: false,
        expandedIndex: null,
        currentPage: 1,
        pageSize: 10,
        searchQuery: '',
        newsItems: [],
        newsIndex: 0,
        newsTimerId: null
    }

    sendData = () => {
        fetch('/rest/sendData').then((result) => {
            this.refreshInfo();
        });
    }

    toggleRow = (idx) => {
        this.setState(({expandedIndex}) => ({
            expandedIndex: expandedIndex === idx ? null : idx
        }));
    }

    refreshInfo = () => {
        const applyResult = (result) => {
            this.setState({
                pos: result || {},
                isMedicine: (result && result.condition && typeof result.condition.isMedicine !== 'undefined')
                    ? !!result.condition.isMedicine
                    : false
            });
        }

        fetch('/rest/info')
            .then(res => res.ok ? res.json() : Promise.reject())
            .then((result) => {
                applyResult(result || {});
            })
            .catch(() => applyResult({}));
    }

    handleCheckboxChange = (event) => {
        this.setState({
            isMedicine: event.target.checked
        });
    };

    saveCondition = () => {
        let body = {
            isMedicine: this.state.isMedicine
        };
        let requestOpt = {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify(body)
        }
        fetch('/rest/condition', requestOpt)
            .then(res => {
                this.refreshInfo();
            }).catch(resp => {
        })
    }

    totalPages = () => {
        const list = Array.isArray(this.state.pos?.merchants) ? this.state.pos.merchants : [];
        const pages = Math.ceil(list.length / this.state.pageSize);
        return pages > 0 ? pages : 1;
    }

    goToPage = (page) => {
        const total = this.totalPages();
        const p = Math.min(total, Math.max(1, page));
        this.setState({currentPage: p, expandedIndex: null});
    }

    nextPage = () => this.goToPage(this.state.currentPage + 1);
    prevPage = () => this.goToPage(this.state.currentPage - 1);

    componentDidMount() {
        this.refreshInfo();
        this.loadNews();
        // keyboard controls for news
        window.addEventListener('keydown', this.onKeyDown);
    }

    componentWillUnmount() {
        if (this.state.newsTimerId) {
            clearInterval(this.state.newsTimerId);
        }
        window.removeEventListener('keydown', this.onKeyDown);
    }

    loadNews = () => {
        const items = [
            {
                title: "МЭДЭГДЭЛ",
                body: "Цахим төлбөрийн баримтын системийн шинэчлэлийн ажлын хүрээнд буюу Хэрэглэгчийн систем нийлүүлэгч болон ААН байгууллагуудад\n" +
                    "\n" +
                    "зориулсан Цахим төлбөрийн баримт хэвлэх хялбаршуулсан АPI буюу ПОС 2.0 хувилбарыг 2026 оны 1 дүгээр сарын 1-ний өдрөөс эхлэн хаахаар урьдчилан мэдэгдсэн билээ.\n" +
                    "\n" +
                    "Хэрэглэгчдээс ирүүлсэн хүсэлт болон бодит нөхцөл байдлыг харгалзан үзсэний үндсэн дээр ПОС 2.0 хувилбарыг хаах хугацааг 2026 оны 4 дүгээр сарын 1-ний өдөр хүртэл хойшлуулж байгааг үүгээр мэдэгдэж байна.\n" +
                    "\n" +
                    "Иймд илүү боловсронгуй, өргөтгөх боломжтой ПОС 3.0 хувилбар руу шилжих ажлыг дээрх хугацаанаас өмнө багтаан зохион байгуулж, хэрэгжилтийг хангахыг хүсье.\n" +
                    " ",
                url: "",
                startDate: "2026-01-30T00:00:00+08:00",
                endDate: "2026-03-30T00:00:00+08:00"
            },
        ];
        const now = new Date();
        const active = (Array.isArray(items) ? items : [])
            .filter(it => {
                const startOk = it.startDate ? (new Date(it.startDate) <= now) : true;
                const endOk = it.endDate ? (new Date(it.endDate) >= now) : true;
                const hasTitle = typeof it.title === 'string' && it.title.trim().length > 0;
                const hasBody = typeof it.body === 'string' && it.body.trim().length > 0;
                return startOk && endOk && hasTitle && hasBody;
            });

        // Start/refresh rotation timer
        let timerId = this.state.newsTimerId;
        if (timerId) {
            clearInterval(timerId);
            timerId = null;
        }
        if (active.length > 1) {
            timerId = setInterval(() => {
                this.setState(prev => ({
                    newsIndex: (prev.newsIndex + 1) % active.length
                }));
            }, NEWS_ROTATE_MS);
        }
        this.setState({newsItems: active, newsIndex: 0, newsTimerId: timerId});
    }

    // Manual news controls
    setNewsIndex = (nextIndex) => {
        const len = this.state.newsItems.length;
        if (len === 0) return;
        const idx = ((nextIndex % len) + len) % len;
        // reset timer to keep rotation smooth after manual change
        if (this.state.newsTimerId && len > 1) {
            clearInterval(this.state.newsTimerId);
            const timerId = setInterval(() => {
                this.setState(prev => ({newsIndex: (prev.newsIndex + 1) % len}));
            }, NEWS_ROTATE_MS);
            this.setState({newsIndex: idx, newsTimerId: timerId});
        } else {
            this.setState({newsIndex: idx});
        }
    }

    prevNews = () => this.setNewsIndex(this.state.newsIndex - 1);
    nextNews = () => this.setNewsIndex(this.state.newsIndex + 1);

    onKeyDown = (e) => {
        if (!this.state.newsItems.length) return;
        if (e.key === 'ArrowLeft') this.prevNews();
        if (e.key === 'ArrowRight') this.nextNews();
    }

    render() {
        return (
            <div className="row">
                <div className="col-12 col-lg-4">
                    <div className="d-flex justify-content-between align-items-center">
                        <p style={{textAlign: 'start', fontWeight: '700', color: "#2C6A8C"}}>Ерөнхий мэдэээлэл</p>
                        <button
                            type="button"
                            className="btn btn-primary border btn-sm"
                            style={{backgroundColor: '#2C6A8C', borderRadius: '10px'}}
                            onClick={() => this.sendData()}
                        >
                            Мэдээлэл илгээх
                        </button>
                    </div>
                    <Row>
                        <Col xs={12} sm={6} className="mb-1">
                            <GeneralInfoCard
                                title="ID:"
                                value={(this.state.pos && this.state.pos.posId) || ''}
                                Icon="security-card"
                            />
                        </Col>
                        <Col xs={12} sm={6} className="mb-1">
                            <GeneralInfoCard
                                title="Дугаар:"
                                value={(this.state.pos && this.state.pos.posNo) || ''}
                                Icon="firstline"
                            />
                        </Col>
                        <Col xs={12} sm={6} className="mb-1">
                            <GeneralInfoCard
                                title="Нийт сугалаа:"
                                value={(this.state.pos && (this.state.pos.leftLotteries || this.state.pos.totalLotteries)) || ''}
                                Icon="receipt"
                            />
                        </Col>
                        <Col xs={12} sm={6} className="mb-1">
                            <GeneralInfoCard
                                title="Сүүлд илгээсэн огноо:"
                                value={(this.state.pos && this.state.pos.lastSentDate) || ''}
                                Icon="calendar"
                            />
                        </Col>
                        <Col xs={12} sm={6} className="mb-1">
                            <GeneralInfoCard
                                title="Database:"
                                value={((this.state.pos && this.state.pos.appInfo) && this.state.pos.appInfo['database-host']) || ''}
                                Icon="cloud"
                            />
                        </Col>
                        <Col xs={12} sm={6} className="mb-1">
                            <GeneralInfoCard
                                title="Ажлын хавтас:"
                                value={((this.state.pos && this.state.pos.appInfo) && this.state.pos.appInfo.workDir) || ''}
                                Icon="folder-minus"
                            />
                        </Col>
                    </Row>

                    <div
                        className="advice-wrap d-flex align-items-center gap-3 mt-2"
                        style={{
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            width: '100%',
                            padding: 10
                        }}
                    >
                        <div style={{display: 'flex', alignItems: 'center', gap: '1rem'}}>
                            <input
                                type="checkbox"
                                style={{
                                    width: 24,
                                    height: 24,
                                    color: '#2C6A8C',
                                    backgroundColor: '#2C6A8C',
                                    borderRadius: '10px'
                                }}
                                className="form-check-input border"
                                checked={this.state.isMedicine}
                                onChange={this.handleCheckboxChange}
                                title="Эмийн сангийн тохиргоо"
                            />
                            <span className="advice-title">
                                Эмийн сангийн үйл ажиллагаа эрхэлдэг
                            </span>
                        </div>

                        <button
                            type="button"
                            className="btn btn-primary border"
                            style={{backgroundColor: '#2C6A8C', borderRadius: '10px'}}
                            onClick={() => this.saveCondition()}
                        >
                            Хадгалах
                        </button>
                    </div>

                    {/* News rotation panel */}
                    {this.state.newsItems.length > 0 && (
                        <div className="news-wrap mt-2" role="region" aria-label="Мэдээ">
                            {(() => {
                                const item = this.state.newsItems[this.state.newsIndex % this.state.newsItems.length];
                                const hasUrl = !!item?.url;
                                const href = hasUrl ? item.url : undefined;
                                // Text-based news content; background is applied to the frame to fill entirely
                                const textBlock = (
                                    <div
                                        className="news-text-block"
                                        style={{
                                            padding: '16px',
                                            minHeight: '120px',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'stretch',
                                            textAlign: 'left',
                                            width: '100%'
                                        }}
                                    >
                                        <div>
                                            <div style={{
                                                fontWeight: 700,
                                                fontSize: '15px',
                                                marginBottom: '8px',
                                                textAlign: 'center'
                                            }}>
                                                {item.title}
                                            </div>
                                            <div style={{
                                                whiteSpace: 'pre-line',
                                                lineHeight: 1.4,
                                                fontSize: '13px',
                                                justifyContent: 'flex-start',
                                            }}>
                                                {item.body}
                                            </div>
                                        </div>
                                    </div>
                                );
                                return (
                                    <div
                                        className="news-frame"
                                        style={{
                                            borderColor: '#91D5FF80',
                                            borderRadius: '10px',
                                            background: 'linear-gradient(180deg, #2C6A8C 0%, #C6E0EE 100%)',
                                            color: '#FFFFFF',
                                            overflow: 'hidden'
                                        }}
                                    >
                                        {hasUrl ? (
                                            <a
                                                href={href}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="news-image-link"
                                                style={{
                                                    display: 'block',
                                                    width: '100%',
                                                    color: 'inherit',
                                                    textDecoration: 'none'
                                                }}
                                            >
                                                {textBlock}
                                            </a>
                                        ) : textBlock}
                                        <div className="news-controls">
                                            <button className="news-btn" onClick={this.prevNews} aria-label="Өмнөх">‹
                                            </button>
                                            {hasUrl && (
                                                <a className="news-detail-btn" href={href} target="_blank"
                                                   rel="noopener noreferrer">Дэлгэрэнгүй</a>
                                            )}
                                            <button className="news-btn" onClick={this.nextNews} aria-label="Дараах">›
                                            </button>
                                        </div>
                                    </div>
                                );
                            })()}
                        </div>
                    )}
                </div>
                <div className="col-12 col-lg-8">
                    <div className="d-flex justify-content-between align-items-center">
                        <p style={{textAlign: 'start', fontWeight: '700', color: "#2C6A8C"}}>Мерчантын жагсаалт</p>
                        <div className="search-wrap">
                            <svg className="search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none"
                                 xmlns="http://www.w3.org/2000/svg">
                                <path d="M21 21l-4.35-4.35M10.5 18a7.5 7.5 0 1 1 0-15 7.5 7.5 0 0 1 0 15z"
                                      stroke="#8C99A5" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            <input
                                type="text"
                                className="search-input"
                                placeholder="Мерчант, түрээслэгчийн ТИН дугаар, нэр оруулах"
                                value={this.state.searchQuery}
                                onChange={(e) => this.setState({
                                    searchQuery: e.target.value,
                                    currentPage: 1,
                                    expandedIndex: null
                                })}
                            />
                        </div>
                    </div>
                    <div className="p-2 mb-0 merchant-table-wrapper">
                        <div className="merchant-table-area">
                            <table className="table table-borderless align-middle merchant-table">
                                <thead>
                                <tr>
                                    <th scope="col">Д/д</th>
                                    <th scope="col">Мерчантын ТИН</th>
                                    <th scope="col">Мерчантын нэр</th>
                                    <th scope="col">НӨАТ суутган төлөгч эсэх</th>
                                    <th scope="col">Түрээслэгч</th>
                                </tr>
                                </thead>
                                <tbody>
                                {(() => {
                                    const merchants = Array.isArray(this.state.pos.merchants) ? this.state.pos.merchants : [];
                                    const q = (this.state.searchQuery || '').trim().toLowerCase();
                                    const matches = (v) => (v ?? '').toString().toLowerCase().includes(q);
                                    const filtered = q
                                        ? merchants.filter(m => {
                                            const mMatch = matches(m?.tin) || matches(m?.name);
                                            const custMatch = Array.isArray(m?.customers) && m.customers.some(c => matches(c?.tin) || matches(c?.name));
                                            return mMatch || custMatch;
                                        })
                                        : merchants;
                                    const total = filtered.length;
                                    const totalPages = Math.max(1, Math.ceil(total / this.state.pageSize));
                                    const page = Math.min(this.state.currentPage, totalPages);
                                    const start = (page - 1) * this.state.pageSize;
                                    const pageItems = filtered.slice(start, start + this.state.pageSize);
                                    return pageItems.length > 0 ? (
                                        pageItems.map((merchant, i) => {
                                            const idx = start + i;
                                            const isVat = merchant?.vatPayer;
                                            const pillClass = isVat === true ? 'pill pill-blue' : (isVat === false ? 'pill pill-red' : 'pill');
                                            const pillText = isVat === true ? 'Тийм' : (isVat === false ? 'Үгүй' : '—');
                                            const isExpanded = this.state.expandedIndex === merchant.tin;
                                            return (
                                                <React.Fragment key={`m_${merchant.tin || idx}`}>
                                                    <tr>
                                                        <td><span className="num-badge">{idx + 1}</span></td>
                                                        <td><a className="tin-link" href="#">{merchant.tin}</a></td>
                                                        <td>{merchant.name}</td>
                                                        <td><span className={pillClass}>{pillText}</span></td>
                                                        <td className="text-center">
                                                            <button
                                                                type="button"
                                                                className="btn action-dots"
                                                                onClick={() => this.toggleRow(merchant.tin)}>
                                                                {isExpanded ? '⋮' : '⋯'}
                                                            </button>
                                                        </td>
                                                    </tr>
                                                    {isExpanded && (
                                                        <tr className="detail-row">
                                                            <td colSpan={5}>
                                                                <div className="detail-panel">
                                                                    <table
                                                                        className="table table-borderless align-middle detail-table m-0">
                                                                        <thead>
                                                                        <tr>
                                                                            <th>Д/д</th>
                                                                            <th>Түрээслэгчийн ТИН</th>
                                                                            <th>Түрээслэгчийн нэр</th>
                                                                            <th>НӨАТ суутган төлөгч эсэх</th>
                                                                            <th></th>
                                                                        </tr>
                                                                        </thead>
                                                                        <tbody>
                                                                        {(() => {
                                                                            const customers = Array.isArray(merchant.customers) ? merchant.customers : [];
                                                                            const list = q ? customers.filter(c => matches(c?.tin) || matches(c?.name)) : customers;
                                                                            return list.length > 0 ? (
                                                                                list.map((customer, cIdx) => (
                                                                                    <tr key={`c_${merchant.tin}_${customer.tin || cIdx}`}>
                                                                                        <td><span
                                                                                            className="num-badge">{cIdx + 1}</span>
                                                                                        </td>
                                                                                        <td><a className="tin-link"
                                                                                               href="#">{customer.tin}</a>
                                                                                        </td>
                                                                                        <td>{customer.name}</td>
                                                                                        <td>
                                                                                <span
                                                                                    className={customer.vatPayer ? 'pill pill-blue' : 'pill pill-red'}>
                                                                                    {customer.vatPayer ? 'Тийм' : 'Үгүй'}
                                                                                </span>
                                                                                        </td>
                                                                                        <td></td>
                                                                                    </tr>
                                                                                ))
                                                                            ) : (
                                                                                <tr>
                                                                                    <td colSpan={5}
                                                                                        className="text-center text-muted">Түрээслэгч
                                                                                        байхгүй
                                                                                    </td>
                                                                                </tr>
                                                                            );
                                                                        })()}
                                                                        </tbody>
                                                                    </table>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )}
                                                </React.Fragment>
                                            );
                                        })
                                    ) : (
                                        <tr>
                                            <td colSpan={5} className="text-center text-muted">Мерчант олдсонгүй</td>
                                        </tr>
                                    );
                                })()}
                                </tbody>
                            </table>
                        </div>

                        {(() => {
                            const list = Array.isArray(this.state.pos.merchants) ? this.state.pos.merchants : [];
                            const q = (this.state.searchQuery || '').trim().toLowerCase();
                            const matches = (v) => (v ?? '').toString().toLowerCase().includes(q);
                            const totalList = q ? list.filter(m => (matches(m?.tin) || matches(m?.name)) || (Array.isArray(m?.customers) && m.customers.some(c => matches(c?.tin) || matches(c?.name)))) : list;
                            return totalList.length > 0 ? (
                                <div className="pagination-bar">
                                    <div className="text-muted small">Нийт: {totalList.length}</div>
                                    <div className="d-flex align-items-center gap-2">
                                        <button className="page-btn" onClick={this.prevPage}
                                                disabled={this.state.currentPage === 1}>‹
                                        </button>
                                        {(() => {
                                            const total = Math.max(1, Math.ceil(totalList.length / this.state.pageSize));
                                            const items = [];
                                            for (let i = 1; i <= total; i++) {
                                                items.push(
                                                    <button key={`pg_${i}`}
                                                            className={`page-btn ${this.state.currentPage === i ? 'active' : ''}`}
                                                            onClick={() => this.goToPage(i)}>{i}</button>
                                                );
                                            }
                                            return items;
                                        })()}
                                        <button className="page-btn" onClick={this.nextPage}
                                                disabled={this.state.currentPage === Math.max(1, Math.ceil(totalList.length / this.state.pageSize))}>›
                                        </button>
                                    </div>
                                </div>
                            ) : null;
                        })()}
                    </div>
                </div>
            </div>
        );
    }
}
